import React, { useCallback, MouseEvent } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'shared/components/Button';
import { getWorkflowApiCredentials } from 'services/workflow/actionCreators';
import { getIsFetchingApiCredentialsFlag } from 'services/workflow/getters';
import { DARK_THEME } from 'shared/constants';
import { colors } from 'lib/prisma-styles';

import { RootState } from '../../../index';

import Destination from '../Destination';

import DestinationInfo from './DestinationInfo';
import CopyableValue from './CopyableValue';
import { DestinationDetailViewerProps } from './types';

const StyledContainer = styled.div`
  max-width: 26.5rem;
  padding: 1.666666667rem;
  background-color: ${({ theme }) => theme.mode === DARK_THEME ? colors.interactive.defaultBackground : 'rgba(51, 55, 61, 0.04)'};
  border-radius: 8px;
`;

const StyledDestination = styled(Destination)`
  margin-right: 1.333333333rem;
`;

const StyledDestinationApiContainer = styled.div`
  padding: 1rem;
  background-color: ${colors.background.section};
  border-radius: 4px;
`;

const StyledDestinationInfo = styled(DestinationInfo)`
  margin-bottom: 1.333333333rem;
`;

// TODO: The use of "unset" for the `min-width` and `min-height` rules will change
// in the near future when we refactor the `Button` component to a less restrictive way.
const StyledButton = styled(Button)`
  && {
    min-width: unset;
    min-height: unset;
    height: 3rem;
    padding: 0.67rem 1rem;
    background-color: #d7dae0;
    color: #33373d;
    font-size: 1rem;
  }
`;

const DestinationDetailViewer = ({
  className,
  destination,
  workflowGuid,
}: DestinationDetailViewerProps) => {
  const REGENERATE_API_CREDENTIALS = true;
  const dispatch = useDispatch();
  const isFetchingApiCredentials = useSelector<RootState, boolean>(
    (state) => getIsFetchingApiCredentialsFlag(state, workflowGuid)
  );

  const onGetCredentialsButtonClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    dispatch(getWorkflowApiCredentials(workflowGuid, destination.type));
  }, [workflowGuid, destination.type]);

  const onRegenerateButtonClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    dispatch(getWorkflowApiCredentials(workflowGuid, destination.type, REGENERATE_API_CREDENTIALS));
  }, [workflowGuid, destination.type]);

  return (
    <StyledContainer className={className}>
      <StyledDestinationInfo label='Destination'>
        <StyledDestination destination={destination} />
      </StyledDestinationInfo>
      {destination.guid && <StyledDestinationInfo label='Enclave ID'>
        <CopyableValue value={destination.guid} />
      </StyledDestinationInfo>}
      {!destination.apiCredentials
        ? <StyledButton
            label='Get API credentials'
            onClick={onGetCredentialsButtonClick}
            isLoading={isFetchingApiCredentials}
          />
        : <StyledDestinationApiContainer>
            <StyledDestinationInfo label='API Key'>
              <CopyableValue value={destination.apiCredentials.apiKey} />
            </StyledDestinationInfo>
            <StyledDestinationInfo label='API Secret'>
              <CopyableValue isSecret value={destination.apiCredentials.apiSecret} />
            </StyledDestinationInfo>
            <StyledButton
              label='Regenerate'
              onClick={onRegenerateButtonClick}
              isLoading={isFetchingApiCredentials}
            />
          </StyledDestinationApiContainer>
      }
    </StyledContainer>
  );
};

export default DestinationDetailViewer;
